/**
 * Única puerta de salida HTTP del front. Todo pasa por acá para que no haya
 * `fetch` sueltos y para que el manejo de error sea uno solo.
 *
 * El front **no** decide permisos: pide, y la API le manda el payload ya
 * recortado por rol. Un 403 se muestra, no se evita adivinando.
 *
 * La identidad viaja en los headers y solo en desarrollo: ver `identidad.ts`.
 */
import { headersDeIdentidad } from './identidad'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Detalle de validación de Zod, cuando la API lo manda. */
    readonly issues?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** El usuario no tiene permiso: el shell muestra la pantalla de sin acceso. */
  get esPermiso(): boolean {
    return this.status === 401 || this.status === 403
  }
}

export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>

/** Serializa los params y descarta los ausentes, para no mandar `?mine=undefined`. */
export function buildUrl(path: string, params?: QueryParams): string {
  if (!params) return path
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs === '' ? path : `${path}?${qs}`
}

const MENSAJE_GENERICO = (status: number) =>
  `La solicitud falló con estado ${status}`

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as {
      error?: string
      message?: string
      issues?: unknown
    }
    return new ApiError(
      response.status,
      body.error ?? 'unknown',
      body.message ?? MENSAJE_GENERICO(response.status),
      body.issues,
    )
  } catch {
    return new ApiError(
      response.status,
      'unknown',
      MENSAJE_GENERICO(response.status),
    )
  }
}

export async function apiGet<T>(
  path: string,
  params?: QueryParams,
): Promise<T> {
  const response = await fetch(buildUrl(path, params), {
    headers: { Accept: 'application/json', ...headersDeIdentidad() },
  })
  if (!response.ok) throw await toApiError(response)
  return (await response.json()) as T
}

async function apiWrite<T>(
  method: 'POST' | 'PUT' | 'PATCH',
  path: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headersDeIdentidad(),
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) throw await toApiError(response)
  return (await response.json()) as T
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiWrite<T>('POST', path, body)
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return apiWrite<T>('PUT', path, body)
}

/** Mensaje mostrable de cualquier error, con fallback en español rioplatense. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return 'Algo falló y no sabemos qué. Probá de nuevo en un momento.'
}
